import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { StarIcon } from "@heroicons/react/20/solid";

import { isValidationError } from "~/components/forms/validationErrorResponse";
import {
  ProductQuickView,
  ProductQuickViewDialog,
} from "~/components/products/product-quick-view";
import { Button } from "~/components/ui/button";
import { TriButton } from "~/components/ui/tri-button";
import { useURL } from "~/contexts";
import { useMediaScreenContext } from "~/contexts/madia-screen";
import type {
  C_ProductInfo,
  Image,
  ProductHitWithSwatches,
  ProductSwatch,
  ProductSwatch as ProductSwatchType,
  VariationGroup,
} from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";
import { cn } from "~/lib/ui";
import { toProductUrl, toVariantURL } from "~/lib/utils/url";
import type { ProductDetailsHystoryState } from "~/routes/product-details/location-state/product-details.location-state";
import { useBasketActions } from "~/routes/resources/basket";
import { WishlistCTA } from "~/routes/resources/wishlist";
import { useFormatPrice } from "~/shop/utils/price";

import { ProductImage } from "../../../components/product-list/product-image";
import * as ProductSwatches from "./product-swatch";

interface ProductHitWithSwatchesExtended extends ProductHitWithSwatches {
  isVariant?: boolean;
}

export const useProductTileQuickView = ({
  product,
  fetcherData,
}: {
  product?: ProductHitWithSwatches;
  fetcherData?: unknown;
}) => {
  const [quickViewProduct, setQuickViewProduct] = useState<
    ProductHitWithSwatches | undefined
  >();
  const [quickViewSwatch, setQuickViewSwatch] = useState<ProductSwatch>();
  const [quickViewFetcherData, setQuickViewFetcherData] = useState<unknown>();

  useEffect(() => {
    if (isValidationError(fetcherData)) {
      setQuickViewProduct(product);
      setQuickViewFetcherData(fetcherData);
    }
  }, [fetcherData, product]);

  function setQuickViewData(data: {
    product: ProductHitWithSwatches;
    swatch: ProductSwatch;
  }) {
    setQuickViewProduct(data.product);
    setQuickViewSwatch(data.swatch);
  }

  function onQuickViewClose() {
    setQuickViewFetcherData(undefined);
    setQuickViewProduct(undefined);
  }

  return {
    setQuickViewProduct,
    onQuickViewClose,
    setQuickViewData,
    quickViewSwatch,
    quickViewProduct,
    quickViewFetcherData,
  };
};

export const useProductTileData = (
  product: ProductHitWithSwatches,
  quickNav?: ProductHitWithSwatches[],
  totalProducts?: number,
) => {
  const swatches = useMemo(() => product.c_swatches || [], [product]);
  const url = useURL();
  const defaultSwatch = useMemo(
    () =>
      swatches.find(s => s.color?.value === product.c_productInfo?.c_color)
        ?.color?.value,
    [swatches, product.c_productInfo?.c_color],
  );
  const getVariantURL = useCallback(
    (variationGroup?: VariationGroup) => {
      if (!variationGroup) return url(toProductUrl(product.c_productInfo));
      return url(toVariantURL(variationGroup));
    },
    [product.c_productInfo, url],
  );

  const getHistoryState = useCallback(
    (
      swatch: ProductSwatchType,
      image?: Image,
    ): ProductDetailsHystoryState | undefined => {
      if (!product.c_productInfo) return undefined;

      return {
        product: product.c_productInfo as SerializeFrom<
          typeof product.c_productInfo
        >,
        swatch,
        image,
        quickNav: quickNav
          ?.map(p => p.c_productInfo)
          .filter(Boolean) as SerializeFrom<C_ProductInfo>[],
        totalProducts,
      };
    },
    [product, quickNav, totalProducts],
  );
  return {
    getHistoryState,
    getVariantURL,
    defaultSwatch,
    swatches,
    url,
  };
};

const ProductTileQuickViewModal = ({
  quickViewProduct,
  quickViewFetcherData,
  swatch,
  onQuickViewClose,
}: {
  quickViewProduct: ProductHitWithSwatches;
  quickViewFetcherData: unknown;
  swatch: ProductSwatch;
  onQuickViewClose: () => void;
}) => {
  return (
    <ProductQuickViewDialog open={true} onClose={onQuickViewClose}>
      <ProductQuickView
        product={quickViewProduct}
        fetcherData={quickViewFetcherData}
        currentSwatch={swatch}
        onClose={onQuickViewClose}
      />
    </ProductQuickViewDialog>
  );
};

const ProductTileComponent = ({
  className,
  product,
  quickNav,
  totalProducts,
  quickNavRef,
  totalProductsRef,
  ref,
  isWishlistTile = false,
}: React.HTMLAttributes<HTMLDivElement> & {
  key: string;
  product: ProductHitWithSwatchesExtended;
  quickNav?: ProductHitWithSwatches[];
  totalProducts?: number;
  quickNavRef?: React.RefObject<ProductHitWithSwatches[]>;
  totalProductsRef?: React.RefObject<number>;
  ref?: React.Ref<HTMLDivElement>;
  isWishlistTile?: boolean;
}) => {
  const basket = useBasketActions();
  const screen = useMediaScreenContext();
  const isMobileLike = ["sm", "md"].includes(screen.is);
  const {
    quickViewProduct,
    quickViewFetcherData,
    onQuickViewClose,
    setQuickViewProduct,
  } = useProductTileQuickView({
    product,
    fetcherData: basket.fetcher.data,
  });
  const formatPrice = useFormatPrice();
  const [isHovered, setIsHovered] = useState(false);

  // Use ref data when available, fallback to prop data
  const actualQuickNav = quickNavRef?.current ?? quickNav;
  const actualTotalProducts = totalProductsRef?.current ?? totalProducts;

  const { getHistoryState, getVariantURL, defaultSwatch, swatches } =
    useProductTileData(product, actualQuickNav, actualTotalProducts);

  return (
    <ProductSwatches.SwatchesProvider
      swatches={swatches}
      ref={ref}
      key={product.productId}
      className={cn(
        "flex w-full flex-col items-center justify-center p-6 sm:w-1/2 md:w-1/3 xl:w-1/4",
        className,
      )}
      defaultValue={defaultSwatch}
    >
      {swatch => {
        const standardPrice = swatch?.variationGroup?.c_price?.standardprice;
        const salesPrice = swatch?.variationGroup?.c_price?.salesprice;

        return (
          <div className="relative">
            <div className="relative">
              {quickViewProduct && (
                <ProductTileQuickViewModal
                  quickViewProduct={quickViewProduct}
                  quickViewFetcherData={quickViewFetcherData}
                  swatch={swatch}
                  onQuickViewClose={onQuickViewClose}
                />
              )}
              <Link
                to={getVariantURL(swatch.variationGroup)}
                className="rounded-xl md:min-h-[310px] lg:min-h-[317px] xl:min-h-[377px]"
                onMouseMove={
                  !isMobileLike ? () => setIsHovered(true) : undefined
                }
                onMouseLeave={
                  !isMobileLike ? () => setIsHovered(false) : undefined
                }
                prefetch={isMobileLike ? "viewport" : "intent"}
                state={getHistoryState(
                  swatch,
                  swatch?.images.large?.images[isHovered ? 1 : 0] ||
                    product.image,
                )}
              >
                <ProductImage
                  image={
                    swatch?.images.large?.images[isHovered ? 1 : 0] ||
                    product.image
                  }
                  type="plp"
                  className="min-h-[320px] rounded-xl"
                  small
                />
              </Link>
              {!isWishlistTile && (
                <WishlistCTA
                  productId={product.productId}
                  className="absolute right-3 top-[13px]"
                  iconHeight={10}
                  iconWidth={10}
                />
              )}
              <div className="relative min-h-[110px] w-full">
                {swatches.length > 0 && (
                  <div className="absolute -top-10 h-10 w-full drop-shadow-white">
                    <ProductSwatches.SwatchList
                      className="justify-start px-2 text-left"
                      max={5}
                    />
                  </div>
                )}
                <Link
                  to={getVariantURL(swatch.variationGroup)}
                  state={getHistoryState(
                    swatch,
                    swatch?.images.large?.images[isHovered ? 1 : 0] ||
                      product.image,
                  )}
                >
                  <p className="text-dark-on-light flex w-full pt-2 text-left text-xs font-semibold">
                    {product.productName}
                  </p>
                </Link>
                <p className="text-dark-on-light pt-2 text-left text-xs font-light">
                  {standardPrice ? (
                    <>
                      <span className="line-through">
                        {formatPrice(standardPrice)}
                      </span>
                      <span className="pl-1 text-salePrice">
                        {formatPrice(salesPrice)}
                      </span>
                    </>
                  ) : (
                    formatPrice(salesPrice)
                  )}
                </p>
                <div className="flex items-center pt-2">
                  {[0, 1, 2, 3, 4].map(rating => (
                    <StarIcon
                      key={rating}
                      className={cn(
                        Math.round(
                          parseFloat(
                            product?.c_productInfo?.c_bvAverageRating || "1.0",
                          ),
                        ) > rating
                          ? "text-neutralalpha-11"
                          : "text-transparent",
                        "h-[12px] w-[12px] flex-shrink-0 stroke-neutralalpha-11",
                      )}
                      aria-hidden="true"
                    />
                  ))}
                </div>
                {isWishlistTile &&
                  product.isVariant &&
                  product.c_productInfo?.variationValues &&
                  Object.entries(product.c_productInfo?.variationValues).map(
                    ([key, value]) => {
                      return (
                        <p
                          key={`${key}-${value}`}
                          className="text-dark-on-light pt-2 text-left text-xs font-light capitalize"
                        >
                          {key}: {String(value)}
                        </p>
                      );
                    },
                  )}
                <Button
                  variant="outline"
                  className="mt-2 block justify-start py-0 text-sm font-semibold text-neutral-11"
                  onClick={() => {
                    if (isWishlistTile && product.isVariant) {
                      basket.add({ productId: product.productId }, 1);
                      return;
                    }
                    setQuickViewProduct(product);
                  }}
                >
                  Add to bag
                </Button>
                {isWishlistTile && (
                  <WishlistCTA
                    productId={product.productId}
                    className="flex"
                    isWishlistPage={true}
                  >
                    <TriButton
                      variant="link"
                      className={cn("self-end hover:no-underline", className)}
                    >
                      <span className="text-sm font-light text-remove hover:underline">
                        Remove
                      </span>
                    </TriButton>
                  </WishlistCTA>
                )}
              </div>
            </div>
          </div>
        );
      }}
    </ProductSwatches.SwatchesProvider>
  );
};
ProductTileComponent.displayName = "ProductTileComponent";

export const ProductTile = memo(
  ProductTileComponent,
  (prevProps, nextProps) => {
    // Skip re-render if core product data hasn't changed
    if (
      prevProps.product.productId === nextProps.product.productId &&
      prevProps.className === nextProps.className &&
      prevProps.isWishlistTile === nextProps.isWishlistTile
    ) {
      // If using refs, props won't change so always skip re-render
      if (prevProps.quickNavRef && prevProps.totalProductsRef) {
        return true;
      }

      // For regular props, check if values changed
      if (prevProps.totalProducts === nextProps.totalProducts) {
        const prevIndex =
          prevProps.quickNav?.findIndex(
            p => p.productId === prevProps.product.productId,
          ) ?? -1;
        const nextIndex =
          nextProps.quickNav?.findIndex(
            p => p.productId === nextProps.product.productId,
          ) ?? -1;
        return prevIndex === nextIndex;
      }
    }
    return false;
  },
);
ProductTile.displayName = "ProductTile";
